<script>
  import { onMount, onDestroy } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate } from '@fortawesome/pro-light-svg-icons';
  import { exportToExcel, generateExportFilename } from '$utils/export.js';
  import { formatToLocalDateTime } from '$utils/dateUtils.js';
  const browser = typeof window !== 'undefined';

  const API_BASE_URL = '/api/proxy';

  let reportData = [];
  let resultCount = 0;
  let isLoading = true;
  let isExporting = false;
  let hasError = false;
  let errorMessage = '';
  let selectedEventType = 'BATTERY_DEAD';
  let startDate = '';
  let endDate = '';

  let currentFetch = null;

  onDestroy(() => {
    if (currentFetch) {
      currentFetch.abort();
    }
  });

  onMount(async () => {
    if (!browser) return;

    if (!$selectedParticipant) {
      goto('/studies');
      return;
    }

    fetchReportData();
  });

  function createAbortController() {
    if (currentFetch) {
      currentFetch.abort();
    }
    return new AbortController();
  }

  async function fetchReportData() {
    const abortController = createAbortController();
    currentFetch = abortController;

    if (!$selectedParticipant || !$selectedParticipant._id) {
      reportData = [];
      resultCount = 0;
      return;
    }

    try {
      isLoading = true;
      hasError = false;

      let url = `${API_BASE_URL}/eventLog?subjectGUID=${$selectedParticipant._id}`;

      if (selectedEventType !== 'ALL') {
        url += `&eventType=${selectedEventType}`;
      }

      if (startDate) url += `&startDate=${startDate}`;
      if (endDate) url += `&endDate=${endDate}`;

      const res = await fetch(url, { signal: abortController.signal });

      if (currentFetch !== abortController) return;

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(`Failed to fetch event report: ${res.status} ${errorData.message || ''}`);
      }

      const responseData = await res.json();

      if (currentFetch === abortController) {
        resultCount = responseData.totalCount || 0;
        reportData = responseData.data || [];
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Error fetching event report:', err);
      reportData = [];
      resultCount = 0;
      hasError = true;
      errorMessage = err.message;
    } finally {
      if (currentFetch === abortController) {
        isLoading = false;
        currentFetch = null;
      }
    }
  }

  function handleRefresh() {
    fetchReportData();
  }

  function handleEventTypeChange() {
    fetchReportData();
  }

  function formatDateTime(dateStr) {
    if (!dateStr) return '—';
    return formatToLocalDateTime(dateStr, $selectedParticipant?.timezoneCode || 'UTC');
  }

  function formatDuration(ms) {
    if (ms == null) return '';
    const totalMinutes = Math.floor(ms / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    parts.push(`${minutes}m`);
    return parts.join(' ');
  }

  function getEventTypeLabel(eventType) {
    switch (eventType) {
      case 'BATTERY_DEAD': return 'Battery Dead';
      case 'SENSOR_ERROR': return 'Sensor Error';
      default: return eventType;
    }
  }

  function getEventDetails(item) {
    if (item.eventType === 'SENSOR_ERROR') {
      return `Sensor: ${item.details?.sensorCode || '—'}`;
    }
    if (item.eventType === 'BATTERY_DEAD') {
      if (item.endTimestamp) {
        return `Recovered after ${formatDuration(item.durationMs)}`;
      }
      return 'Not recovered';
    }
    return '';
  }

  async function handleExportExcel() {
    try {
      isExporting = true;

      const filename = generateExportFilename({
        protocolNumber: $selectedStudy?.protocolNumber,
        subjectId: $selectedParticipant?.subjectId,
        reportType: 'EventReport'
      });

      const formattedData = reportData.map((item) => ({
        Protocol: $selectedStudy?.protocolNumber || '',
        Participant: $selectedParticipant?.subjectId || '',
        'Event Type': getEventTypeLabel(item.eventType),
        Timestamp: item.timestamp ? formatDateTime(item.timestamp) : '',
        'End Timestamp': item.endTimestamp ? formatDateTime(item.endTimestamp) : '',
        Duration: item.durationMs != null ? formatDuration(item.durationMs) : '',
        Details: getEventDetails(item)
      }));

      await exportToExcel(formattedData, filename);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      isExporting = false;
    }
  }
</script>

{#if hasError}
  <div class="p-4 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-100 rounded-lg mb-4">
    <p>An error occurred: {errorMessage}</p>
    <button
      class="mt-2 px-4 py-2 bg-red-200 dark:bg-red-800 rounded hover:bg-red-300 dark:hover:bg-red-700"
      on:click={() => {
        hasError = false;
        errorMessage = '';
        fetchReportData();
      }}
    >
      Try Again
    </button>
  </div>
{/if}

<StudyHeader />

<FeatureInfo
  title="Event Report"
  description="This report shows notable device events for the participant, including periods when the phone battery reached 1% and sensor errors. 
  Events are automatically detected from log entries and cached for fast retrieval."
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails />
{/if}

<Toolbar title="Event Report" itemCountTitle="Events" itemCount={resultCount}>
  <div class="flex items-center gap-4">
    <div class="flex items-baseline gap-2">
      Event Type:
      <select
        bind:value={selectedEventType}
        on:change={handleEventTypeChange}
        class="form-select h-6 w-36 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
      >
        <option value="ALL">All</option>
        <option value="BATTERY_DEAD">Battery Dead</option>
        <option value="SENSOR_ERROR">Sensor Error</option>
      </select>
    </div>
    <div class="flex items-baseline gap-2">
      Start:
      <input
        type="date"
        bind:value={startDate}
        on:change={handleRefresh}
        class="h-6 w-32 px-1 border rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100"
      />
    </div>
    <div class="flex items-baseline gap-2">
      End:
      <input
        type="date"
        bind:value={endDate}
        on:change={handleRefresh}
        class="h-6 w-32 px-1 border rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100"
      />
    </div>
    {#if startDate || endDate}
      <button
        on:click={() => { startDate = ''; endDate = ''; handleRefresh(); }}
        class="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline"
      >clear</button>
    {/if}
    <button on:click={handleRefresh} class="pagination mx-1 p-0" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

<div class="px-1 pt-0">
  {#if isLoading}
    <div class="flex justify-center items-center py-8">
      <div class="loader"></div>
    </div>
  {:else if reportData.length > 0}
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Event Type</th>
          <th class="px-4 py-2 text-left bold">Timestamp (Local)</th>
          <th class="px-4 py-2 text-left bold">End Timestamp (Local)</th>
          <th class="px-4 py-2 text-left bold">Duration</th>
          <th class="px-4 py-2 text-left bold">Details</th>
        </tr>
      </thead>
      <tbody>
        {#each reportData as item, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2">
              <span
                class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                class:text-red-600={item.eventType === 'BATTERY_DEAD'}
                class:ring-red-500={item.eventType === 'BATTERY_DEAD'}
                class:text-amber-600={item.eventType === 'SENSOR_ERROR'}
                class:ring-amber-500={item.eventType === 'SENSOR_ERROR'}
              >
                {getEventTypeLabel(item.eventType)}
              </span>
            </td>
            <td class="px-4 py-2 text-xs">{formatDateTime(item.timestamp)}</td>
            <td class="px-4 py-2 text-xs">
              {#if item.endTimestamp}
                {formatDateTime(item.endTimestamp)}
              {:else if item.eventType === 'BATTERY_DEAD'}
                <span class="text-amber-600 dark:text-amber-400 font-medium">Ongoing</span>
              {:else}
                —
              {/if}
            </td>
            <td class="px-4 py-2 text-xs">
              {#if item.durationMs != null}
                {formatDuration(item.durationMs)}
              {:else if item.eventType === 'BATTERY_DEAD' && !item.endTimestamp}
                <span class="text-amber-600 dark:text-amber-400 font-medium">Ongoing</span>
              {:else}
                —
              {/if}
            </td>
            <td class="px-4 py-2 text-xs">
              {#if item.eventType === 'SENSOR_ERROR'}
                <span class="font-mono">Sensor: {item.details?.sensorCode || '—'}</span>
                {#if item.details?.message}
                  <br /><span class="text-gray-500 dark:text-gray-400">{item.details.message}</span>
                {/if}
              {:else if item.eventType === 'BATTERY_DEAD'}
                {#if item.endTimestamp}
                  Recovered
                {:else}
                  Not recovered
                {/if}
              {/if}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  {:else}
    <div class="text-center py-8 text-gray-500 dark:text-gray-400">
      No events found for this participant
    </div>
  {/if}
</div>

<Footer>
  <div></div>
  <div>
    <button
      on:click={handleExportExcel}
      disabled={reportData.length === 0}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs disabled:opacity-50"
      >Excel</button
    >
  </div>
</Footer>

{#if isExporting}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-700 dark:text-gray-300">Preparing Excel export...</p>
    </div>
  </div>
{/if}

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .fixed {
    position: fixed;
  }

  .inset-0 {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .bg-opacity-50 {
    --tw-bg-opacity: 0.5;
  }

  .z-50 {
    z-index: 50;
  }

  .shadow-lg {
    --tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color),
      0 4px 6px -4px var(--tw-shadow-color);
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000),
      var(--tw-shadow);
  }
</style>
